import type {
  StoryDetail,
  StoryListItem,
  StoryValidationIssue,
  StoryValidationResult,
  StoryVersionDetail,
  StoryVersionListItem,
} from "@actale/contracts";
import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { api, ApiError } from "./api.js";
import { formatUpdatedAt, storyStatusLabel } from "./story-utils.js";

function nextVersionLabel(versions: StoryVersionListItem[]): string {
  const current = versions[0]?.versionLabel.replace(/^v/, "");
  const parts = current?.match(/^(\d+)\.(\d+)\.(\d+)$/);
  if (!parts) return "1.0.0";
  return `${parts[1]}.${parts[2]}.${Number(parts[3]) + 1}`;
}

function ValidationIssueRow({ issue }: { issue: StoryValidationIssue }): React.ReactNode {
  return (
    <li className={`release-issue ${issue.severity.toLowerCase()}`}>
      <span>{issue.severity === "ERROR" ? "阻止" : "提醒"}</span>
      <div>
        <strong>{issue.message}</strong>
        <code>{issue.code} · {issue.path}</code>
      </div>
    </li>
  );
}

function VersionDetailPanel({ version }: { version: StoryVersionDetail }): React.ReactNode {
  return (
    <section className="release-version-detail" aria-label="已发布版本内容">
      <header>
        <div>
          <p className="eyebrow">已发布版本</p>
          <h3>{version.versionLabel} 版本内容</h3>
        </div>
        <span>R{version.draftRevision}</span>
      </header>
      <dl className="release-facts">
        <div><dt>角色</dt><dd>{version.characterCount}</dd></div>
        <div><dt>场景</dt><dd>{version.sceneCount}</dd></div>
        <div><dt>结局</dt><dd>{version.endingCount}</dd></div>
        <div><dt>可扮演</dt><dd>{version.appSnapshot.playableRoles.length}</dd></div>
      </dl>
      <div className="release-snapshot-copy">
        <strong>玩家可见内容</strong>
        <p>{version.appSnapshot.story.summary}</p>
        <div className="release-role-tags">
          {version.appSnapshot.playableRoles.map((role) => <span key={role.key}>{role.name}</span>)}
        </div>
        <small>开场：{version.appSnapshot.openingScene.title} · 已裁剪私密知识、私人档案、结局条件与导演提示</small>
      </div>
      <details className="release-technical-details">
        <summary>技术详情</summary>
        <div className="release-checksum">
          <span>SHA-256</span>
          <code title={version.checksum}>{version.checksum}</code>
        </div>
        <div className="release-api-paths">
          <strong>APP 读取接口</strong>
          <code>/api/v1/story-lines/{version.storySlug}</code>
          <code>/api/v1/story-versions/{version.id}/runtime</code>
        </div>
      </details>
    </section>
  );
}

export function VersionPublishWorkbench({
  initialStoryId = "",
  canPublish,
  onEditStory,
}: {
  initialStoryId?: string;
  canPublish: boolean;
  onEditStory?: (storyId: string) => void;
}): React.ReactNode {
  const [stories, setStories] = useState<StoryListItem[]>([]);
  const [storyId, setStoryId] = useState(initialStoryId);
  const [story, setStory] = useState<StoryDetail | null>(null);
  const [validation, setValidation] = useState<StoryValidationResult | null>(null);
  const [versions, setVersions] = useState<StoryVersionListItem[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<StoryVersionDetail | null>(null);
  const [versionLabel, setVersionLabel] = useState("1.0.0");
  const [releaseNotes, setReleaseNotes] = useState("");
  const [acknowledgeWarnings, setAcknowledgeWarnings] = useState(false);
  const [publishKey, setPublishKey] = useState("");
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState<"validate" | "publish" | "version" | "">("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    let active = true;
    api.storyLines({ page: 1, pageSize: 100 }).then((result) => {
      if (!active) return;
      setStories(result.data);
      setStoryId((current) => current || initialStoryId || result.data.find((item) => item.status !== "ARCHIVED")?.id || "");
    }).catch((caught) => {
      if (active) setError(caught instanceof ApiError ? caught.message : "无法读取故事线");
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [initialStoryId]);

  const loadStory = useCallback(async (id: string) => {
    if (!id) {
      setStory(null);
      setValidation(null);
      setVersions([]);
      setSelectedVersion(null);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const [storyDetail, validationResult, versionList] = await Promise.all([
        api.storyDetail(id),
        api.validationResult(id),
        api.storyVersions(id),
      ]);
      setStory(storyDetail);
      setValidation(validationResult.data);
      setVersions(versionList);
      setVersionLabel(nextVersionLabel(versionList));
      setSelectedVersion(versionList[0] ? await api.storyVersion(versionList[0].id) : null);
      setReleaseNotes("");
      setAcknowledgeWarnings(false);
      setPublishKey("");
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "无法读取发布资料");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void loadStory(storyId); }, [loadStory, storyId]);

  const validationCurrent = Boolean(story && validation?.draftRevision === story.draftRevision);
  const currentPublishedVersion = story
    ? versions.find((version) => version.draftRevision === story.draftRevision) ?? null
    : null;
  const revisionAlreadyPublished = Boolean(currentPublishedVersion);
  const publishBlocked = !story || story.status === "ARCHIVED" || !validationCurrent || Boolean(validation?.errorCount) || !canPublish || revisionAlreadyPublished;
  const issueGroups = useMemo(() => ({
    errors: validation?.issues.filter((issue) => issue.severity === "ERROR") ?? [],
    warnings: validation?.issues.filter((issue) => issue.severity === "WARNING") ?? [],
  }), [validation]);

  async function runValidation(): Promise<void> {
    if (!story) return;
    setWorking("validate");
    setError("");
    setNotice("");
    try {
      const result = await api.validateStory(story.id, story.draftRevision);
      setValidation(result);
      const freshStory = await api.storyDetail(story.id);
      setStory(freshStory);
      setStories((current) => current.map((item) => item.id === freshStory.id ? { ...item, status: freshStory.status, draftRevision: freshStory.draftRevision } : item));
      setNotice(result.errorCount ? "验证完成，仍有阻止发布的问题。" : "验证完成，当前修订可以进入发布确认。 ");
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "验证失败");
    } finally {
      setWorking("");
    }
  }

  async function publish(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!story || publishBlocked) return;
    setWorking("publish");
    setError("");
    setNotice("");
    const idempotencyKey = publishKey || crypto.randomUUID();
    setPublishKey(idempotencyKey);
    try {
      const result = await api.publishStory(story.id, {
        expectedRevision: story.draftRevision,
        versionLabel,
        releaseNotes,
        idempotencyKey,
        acknowledgeWarnings,
      });
      setNotice(result.idempotentReplay ? "已恢复同一发布请求的结果。" : `${result.data.versionLabel} 已发布，APP API 现在可读取该版本。`);
      setSelectedVersion(result.data);
      setPublishKey("");
      const [freshStory, freshVersions] = await Promise.all([api.storyDetail(story.id), api.storyVersions(story.id)]);
      setStory(freshStory);
      setVersions(freshVersions);
      setStories((current) => current.map((item) => item.id === freshStory.id ? { ...item, status: freshStory.status, draftRevision: freshStory.draftRevision } : item));
      setVersionLabel(nextVersionLabel(freshVersions));
      setReleaseNotes("");
      setAcknowledgeWarnings(false);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "发布失败");
    } finally {
      setWorking("");
    }
  }

  async function openVersion(id: string): Promise<void> {
    setWorking("version");
    setError("");
    try {
      setSelectedVersion(await api.storyVersion(id));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "无法读取版本快照");
    } finally {
      setWorking("");
    }
  }

  async function showPublishedSnapshot(): Promise<void> {
    if (!currentPublishedVersion) return;
    await openVersion(currentPublishedVersion.id);
    window.requestAnimationFrame(() => {
      document.querySelector(".release-history")?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  return (
    <div className="release-workbench">
      <div className="page-heading release-heading">
        <div>
          <p className="eyebrow">内容发布</p>
          <h1>版本与验证</h1>
          <p>检查故事草稿并发布版本，供APP使用。</p>
        </div>
        <label className="release-story-select">
          <span>当前故事线</span>
          <select value={storyId} onChange={(event) => setStoryId(event.target.value)} disabled={loading || working !== ""}>
            {!stories.length ? <option value="">暂无故事线</option> : null}
            {stories.map((item) => <option key={item.id} value={item.id}>{item.title} · R{item.draftRevision} · {storyStatusLabel(item.status)}</option>)}
          </select>
        </label>
      </div>

      {error ? <div className="release-banner error" role="alert"><span>!</span>{error}</div> : null}
      {notice ? <div className="release-banner success" role="status"><span>✓</span>{notice}</div> : null}
      {loading ? <div className="release-loading"><span /><span /><span /></div> : null}

      {!loading && story ? (
        <>
          <section className="release-overview">
            <div>
              <span className={`status-pill status-${story.status.toLowerCase()}`}>{storyStatusLabel(story.status)}</span>
              <strong>{story.title}</strong>
              <small>{story.slug} · 草稿 R{story.draftRevision}</small>
            </div>
            <dl>
              <div><dt>完成度</dt><dd>{story.completion.score}%</dd></div>
              <div><dt>阻止项</dt><dd>{validationCurrent ? validation?.errorCount ?? 0 : "—"}</dd></div>
              <div><dt>警告</dt><dd>{validationCurrent ? validation?.warningCount ?? 0 : "—"}</dd></div>
              <div><dt>已发布</dt><dd>{versions.length}</dd></div>
            </dl>
          </section>

          <div className="release-grid">
            <section className="release-panel validation-panel">
              <header>
                <div><span>01</span><div><strong>自动验证</strong><small>每次发布前都会再次检查</small></div></div>
                <button className="secondary-button" onClick={() => void runValidation()} disabled={working !== "" || story.status === "ARCHIVED"}>{working === "validate" ? "正在验证…" : "运行验证"}</button>
              </header>
              {!validationCurrent ? (
                <div className="release-empty-check">
                  <span>待验证</span>
                  <h3>当前 R{story.draftRevision} 还没有验证结果</h3>
                  <p>发布前检查结构、引用、模拟覆盖和未解决问题。</p>
                </div>
              ) : (
                <>
                  <div className={`release-score ${validation!.errorCount ? "blocked" : "ready"}`}>
                    <strong>{validation!.errorCount ? "暂不可发布" : "结构验证通过"}</strong>
                    <span>{validation!.score}% · R{validation!.draftRevision}</span>
                    <small>验证于 {formatUpdatedAt(validation!.validatedAt)}</small>
                  </div>
                  {!validation!.issues.length ? <div className="release-all-clear">✓ 没有阻止项或警告</div> : null}
                  {issueGroups.errors.length ? <><h4>阻止发布 · {issueGroups.errors.length}</h4><ul className="release-issues">{issueGroups.errors.map((issue) => <ValidationIssueRow key={`${issue.code}-${issue.path}`} issue={issue} />)}</ul></> : null}
                  {issueGroups.warnings.length ? <><h4>发布提醒 · {issueGroups.warnings.length}</h4><ul className="release-issues">{issueGroups.warnings.map((issue) => <ValidationIssueRow key={`${issue.code}-${issue.path}-${issue.message}`} issue={issue} />)}</ul></> : null}
                </>
              )}
            </section>

            {currentPublishedVersion ? (
              <section className="release-panel published-result-panel" aria-label="已发布结果">
                <header>
                  <div><span>02</span><div><strong>已发布结果</strong><small>当前修订已经冻结</small></div></div>
                  <span className="published-result-status">APP 可读取</span>
                </header>
                <div className="published-result-hero">
                  <span aria-hidden="true">✓</span>
                  <div>
                    <small>已发布版本</small>
                    <h3>{currentPublishedVersion.versionLabel} 已发布</h3>
                    <p>发布流程已经完成，不需要再次确认。</p>
                  </div>
                </div>
                <dl className="published-result-facts">
                  <div><dt>发布时间</dt><dd>{formatUpdatedAt(currentPublishedVersion.publishedAt)}</dd></div>
                  <div><dt>操作人</dt><dd>{currentPublishedVersion.publishedBy?.displayName ?? "系统"}</dd></div>
                  <div><dt>冻结修订</dt><dd>R{currentPublishedVersion.draftRevision}</dd></div>
                  <div><dt>发布时警告</dt><dd>{validationCurrent ? validation?.warningCount ?? 0 : "—"}</dd></div>
                </dl>
                {currentPublishedVersion.releaseNotes ? (
                  <div className="published-result-notes">
                    <strong>发布说明</strong>
                    <p>{currentPublishedVersion.releaseNotes}</p>
                  </div>
                ) : null}
                <div className="release-lock-note"><span>锁</span><p>已发布的版本内容不可修改；继续编辑会生成新的草稿修订。</p></div>
                <div className="published-result-actions">
                  <button type="button" className="primary-button" onClick={() => void showPublishedSnapshot()} disabled={working !== ""}>查看发布快照</button>
                  {onEditStory ? <button type="button" className="secondary-button" onClick={() => onEditStory(story.id)} disabled={working !== ""}>编辑并创建新修订</button> : null}
                </div>
              </section>
            ) : (
              <form className="release-panel publish-panel" onSubmit={publish}>
                <header>
                  <div><span>02</span><div><strong>发布版本</strong><small>仅超级管理员可执行</small></div></div>
                </header>
                {!canPublish ? <div className="release-permission">编辑账号可以验证，但不能发布版本。</div> : null}
                <label><span>版本号</span><input value={versionLabel} onChange={(event) => { setVersionLabel(event.target.value); setPublishKey(""); }} placeholder="1.0.0" pattern="v?\d+\.\d+\.\d+(?:-[a-z0-9.-]+)?" required /></label>
                <label><span>发布说明</span><textarea value={releaseNotes} onChange={(event) => { setReleaseNotes(event.target.value); setPublishKey(""); }} rows={5} maxLength={5000} placeholder="说明本版本的故事内容和测试结论" /></label>
                {validationCurrent && Boolean(validation?.warningCount) ? (
                  <label className="release-ack"><input type="checkbox" checked={acknowledgeWarnings} onChange={(event) => setAcknowledgeWarnings(event.target.checked)} /><span>我已查看并接受当前 {validation!.warningCount} 条警告</span></label>
                ) : null}
                <div className="release-lock-note"><span>锁</span><p>发布后的版本内容不可修改；继续编辑会生成新的草稿修订。</p></div>
                <button className="primary-button" disabled={publishBlocked || working !== "" || Boolean(validation?.warningCount && !acknowledgeWarnings)}>{working === "publish" ? "正在冻结并发布…" : "确认发布版本"}</button>
                {publishBlocked ? <small className="release-block-reason">{!canPublish ? "需要超级管理员权限" : !validationCurrent ? "请先验证当前修订" : validation?.errorCount ? "请先解决全部阻止项" : story.status === "ARCHIVED" ? "归档故事线不能发布" : "当前不可发布"}</small> : null}
              </form>
            )}
          </div>

          <section className="release-history">
            <header><div><p className="eyebrow">历史记录</p><h2>版本历史</h2></div><span>{versions.length} 个已发布版本</span></header>
            {!versions.length ? <div className="release-history-empty"><strong>尚未发布版本</strong><p>通过验证后，第一个版本会出现在这里。</p></div> : (
              <div className="release-history-grid">
                <div className="release-version-list">
                  {versions.map((version) => (
                    <button key={version.id} className={selectedVersion?.id === version.id ? "active" : ""} onClick={() => void openVersion(version.id)} disabled={working === "version"}>
                      <span className="release-version-dot" />
                      <span><strong>{version.versionLabel}</strong><small>草稿 R{version.draftRevision} · {formatUpdatedAt(version.publishedAt)}</small></span>
                      <em>{version.publishedBy?.displayName ?? "系统"}</em>
                    </button>
                  ))}
                </div>
                {selectedVersion ? <VersionDetailPanel version={selectedVersion} /> : null}
              </div>
            )}
          </section>
        </>
      ) : null}
    </div>
  );
}
